<template>
  <div v-editable="props.stepData" class="step-selection">
    <header>
      <h2 class="h6">
        {{ props.stepData.title }}
      </h2>
      <WizcoTooltip v-if="props.stepData.infotext[0]" :blok="props.stepData.infotext[0]" />
    </header>
    <div class="options-wrapper" :class="[props.stepData.variant]">
      <div
        v-if="!props.stepData.variant || props.stepData.variant === 'tile'"
        class="tile-container"
      >
        <div
          v-for="optionData in props.stepData.options"
          :key="optionData._uid"
          v-editable="optionData"
          class="tile"
          :class="[
            {
              selected: selectedOptions.includes(optionData.value)
            },
            { noImage: !optionData.icon.id }
          ]"
          @click="onCheckTileOption(optionData)"
        >
          <img
            v-if="optionData.icon.id"
            :src="`${optionData.icon.filename}/m/80x0`"
            :alt="optionData.icon.alt"
            :title="optionData.icon.title"
            class="image"
            loading="lazy"
          />
          <div class="label">
            {{ optionData.label }}
          </div>
        </div>
      </div>
      <div v-else class="sc-container">
        <div v-if="props.stepData.isMultiselect" class="checkbox-container">
          <PflegeCheckbox
            v-for="optionData in props.stepData.options"
            :key="optionData._uid"
            v-model="selectedOptions"
            v-editable="optionData"
            :label="optionData.label"
            :value="optionData.value"
            required
            :variant="props.stepData.variant"
            @click="onCheckTileOption(optionData)"
          />
        </div>
        <div v-else class="radio-container">
          <PflegeRadio
            v-model="selectedOptions[0]"
            :options="props.stepData.options"
            required
            flow="column"
            :variant="props.stepData.variant ? props.stepData.variant : 'normal'"
            @update:model-value="onCheckTileOption"
          />
        </div>
      </div>
    </div>
    <StepFooter :error-message="stepFooterErrorMessage" @click="nextStepBtnClick()" />
  </div>
</template>

<script setup>
import StepFooter from '../components/footer/StepFooter.vue'
import { useWizcoStore } from '../stores/wizco'
import { ref, onMounted } from 'vue'

const props = defineProps({
  isActive: {
    type: Boolean,
    required: false,
    default: false
  },
  stepData: {
    type: Object,
    required: true
  },
  stepUuid: {
    type: String,
    required: false,
    default: ''
  }
})
const wizco = useWizcoStore()
const actualStepdataRelation = wizco.stepData[props.stepUuid]

const stepFooterErrorMessage = ref('')
const selectedOptions = ref([])
const emmitSelectedOptions = () => {
  actualStepdataRelation.data[props.stepData.fieldKey] = selectedOptions.value ?? []
  wizco.isActiveStepReady = actualStepdataRelation.data[props.stepData.fieldKey].length > 0
}

const onCheckTileOption = (optionData) => {
  const val = optionData.value ? optionData.value : optionData
  if (props.stepData.isMultiselect) {
    const indexOfValueInSelectedOptions = selectedOptions.value.findIndex((row) => row === val)
    if (indexOfValueInSelectedOptions !== -1) {
      selectedOptions.value.splice(indexOfValueInSelectedOptions, 1)
    } else {
      selectedOptions.value.push(val)
    }
  } else {
    selectedOptions.value = [val]
  }
  stepFooterErrorMessage.value = ''
  emmitSelectedOptions()
}

const nextStepBtnClick = () => {
  if (!wizco.isActiveStepReady) {
    stepFooterErrorMessage.value = 'Bitte treffen Sie eine Auswahl.'
    return null
  }
  wizco.submit()
}

onMounted(() => {
  if (actualStepdataRelation.data[props.stepData.fieldKey]) {
    selectedOptions.value = actualStepdataRelation.data[props.stepData.fieldKey]
  }
  emmitSelectedOptions()
})
</script>

<style lang="scss">
.step-selection {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.step-selection header {
  position: relative;
  display: flex;
  gap: 1rem;
}

.step-selection header h2 {
  padding-bottom: 1.33rem;
}

.step-selection header h2:empty {
  display: none;
}

.step-selection header .infotooltip {
  flex-shrink: 0;
}

.step-selection .options-wrapper > .tile-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  max-width: 100%;
}

.step-selection .options-wrapper .tile-container > .tile {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1.66rem;
  padding: 1rem;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 0 4px 0 #0008;
  position: relative;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
}

.step-selection .options-wrapper .tile-container > .tile::before {
  content: '';
  display: block;
}

.step-selection .options-wrapper .tile-container > .tile.noImage {
  padding-inline: 0.75rem;
}

.step-selection .options-wrapper .tile-container > .tile::before.noImage {
  content: none;
}

.step-selection .options-wrapper .tile-container > .tile:not(.v-enter-active) {
  transition-property: border-color, background-color;
  transition-duration: var(--time1);
  transition-timing-function: ease-in-out;
}

.step-selection .options-wrapper .tile-container > .tile.selected {
  border-color: var(--blue);
  box-shadow: 0 0 4px 0 var(--blue);
}

.step-selection .options-wrapper .tile-container > .tile:is(:hover, :focus) {
  background-color: #e2f6ff;
}

.step-selection .options-wrapper .tile-container > .tile .image {
  width: 45px;
  height: 45px;
  object-fit: contain;
  object-position: center;
}

.step-selection .options-wrapper .tile-container > .tile,
.step-selection .options-wrapper .checkbox-container > .checkbox,
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper {
  animation: wk-showtile var(--time2) ease-in-out 0ms forwards;

  /** We need to disable the animation on webkit browsers because it causes
 a restart of the animation for the checkbox on every hover-change. Only 
 happens in Safari > v.17. */
  @supports (hanging-punctuation: first) and (font: -apple-system-body) and
    (-webkit-appearance: none) {
    animation: none;
  }
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(1),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(1),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(1) {
  animation-delay: calc(0 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(2),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(2),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(2) {
  animation-delay: calc(0.5 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(3),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(3),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(3) {
  animation-delay: calc(1 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(4),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(4),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(4) {
  animation-delay: calc(1.5 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(5),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(5),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(5) {
  animation-delay: calc(2 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(6),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(6),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(6) {
  animation-delay: calc(2.5 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(7),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(7),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(7) {
  animation-delay: calc(3 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(8),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(8),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(8) {
  animation-delay: calc(3.5 * var(--time2));
}

.step-selection .options-wrapper .tile-container > .tile:nth-child(9),
.step-selection .options-wrapper .checkbox-container > .checkbox:nth-child(9),
.step-selection .options-wrapper .inputgroup-wrapper > .input-wrapper:nth-child(9) {
  animation-delay: calc(4 * var(--time2));
}

.step-selection .options-wrapper > .sc-container {
  width: 100%;
}

.step-selection .options-wrapper > .sc-container .checkbox-container {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 1rem;
}

@keyframes wk-showtile {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }

  25% {
    opacity: 0;
  }

  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (min-width: 576px) {
  .step-selection {
    gap: 1rem;
  }

  .step-selection .options-wrapper {
    flex-direction: row;
    width: inherit;
    gap: 1rem;
  }

  .step-selection .options-wrapper .tile-container {
    flex-direction: row;
  }

  .step-selection .options-wrapper .tile-container > .tile {
    width: 155px;
    height: 180px;
  }

  .step-selection .options-wrapper .tile-container > .tile:not(.v-enter-active) {
    flex-direction: column;
    justify-content: space-between;
    padding: 1rem;
    gap: 0.5rem;
  }

  .step-selection .options-wrapper .tile-container > .tile.noImage {
    justify-content: center;
  }

  .step-selection .options-wrapper .tile-container > .tile .image {
    width: 80px;
    height: 80px;
  }

  .step-selection .options-wrapper .tile-container > .tile .label {
    font-size: 1rem;
    line-height: 1.4;
    text-align: center;
    word-break: break-word;
  }
}
</style>
